import React, { useEffect, useState, useMemo } from 'react';
import { Users, Clock, Loader2, ChevronDown, ChevronUp, MessageSquare, Target, Filter, Phone, ArrowRight, Calendar } from 'lucide-react';
import { IntelligenceLayout, fetchWithAuth, MetricCard } from './IntelligenceLayout';
import CompanyDetailModal from '../components/CompanyDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PIPELINE_STAGES = ['Prospect', 'Contacted', 'Discovery Call', 'Proposal', 'Client'];
const RESPONSE_STATUSES = ['No Response', 'Interested', 'Meeting Scheduled', 'Client', 'Not a Fit'];

const PIPELINE_COLORS = {
  'Prospect': { bg: 'bg-gray-50 border-gray-300', text: 'text-gray-700', badge: 'bg-gray-200 text-gray-700', bar: '#6b7280' },
  'Contacted': { bg: 'bg-blue-50 border-blue-300', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', bar: '#2563eb' },
  'Discovery Call': { bg: 'bg-amber-50 border-amber-300', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', bar: '#d97706' },
  'Proposal': { bg: 'bg-purple-50 border-purple-300', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', bar: '#7c3aed' },
  'Client': { bg: 'bg-green-50 border-green-300', text: 'text-green-700', badge: 'bg-green-100 text-green-700', bar: '#059669' },
};

const STATUS_DOTS = {
  'No Response': 'bg-gray-400',
  'Interested': 'bg-blue-500',
  'Meeting Scheduled': 'bg-amber-500',
  'Client': 'bg-green-500',
  'Not a Fit': 'bg-red-500',
};

const OutreachCRM = ({ embedded = false }) => {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('updated_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedCompany, setSelectedCompany] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listData, summaryData] = await Promise.all([
        fetchWithAuth(`${API_URL}/api/outreach/list?limit=200`),
        fetchWithAuth(`${API_URL}/api/outreach/pipeline-summary`),
      ]);
      setRecords(listData.records || []);
      setSummary(summaryData);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleSort = (f) => {
    if (sortField === f) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortField(f); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    let arr = [...records];
    if (stageFilter !== 'all') arr = arr.filter(r => r.pipeline_stage === stageFilter);
    if (statusFilter !== 'all') arr = arr.filter(r => r.response_status === statusFilter);
    return arr.sort((a, b) => {
      const m = sortDir === 'desc' ? -1 : 1;
      if (sortField === 'company') return m * (a.company || '').localeCompare(b.company || '');
      if (sortField === 'updated_at') return m * ((a.updated_at || '').localeCompare(b.updated_at || ''));
      return 0;
    });
  }, [records, stageFilter, statusFilter, sortField, sortDir]);

  const stageCounts = summary?.stage_counts || {};
  const statusCounts = summary?.status_counts || {};

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'desc' ? <ChevronDown className="h-3 w-3" /> : <ChevronUp className="h-3 w-3" />;
  };

  const handleModalClose = () => {
    setSelectedCompany(null);
    loadData(); // Refresh data after modal closes
  };

  return (
    <IntelligenceLayout title="Outreach CRM" embedded={embedded}>
      <div className="p-6 space-y-6" data-testid="outreach-crm-page">
        <div>
          <h1 className="text-2xl font-bold text-[#0A1F44]" data-testid="page-title">Outreach CRM & Sales Pipeline</h1>
          <p className="text-sm text-gray-500 mt-1">Track your outreach, manage contacts, and move prospects through your sales pipeline</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
        ) : (
          <>
            {/* Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <MetricCard label="Total Tracked" value={summary?.total_companies || 0} icon={Users} />
              <MetricCard label="Follow-ups Due" value={summary?.followups_due || 0} subtext="Need attention" icon={Clock} />
              <MetricCard label="Interested" value={statusCounts['Interested'] || 0} icon={MessageSquare} />
              <MetricCard label="Meetings" value={statusCounts['Meeting Scheduled'] || 0} icon={Calendar} />
              <MetricCard label="Clients" value={stageCounts['Client'] || 0} icon={Target} />
            </div>

            {/* Pipeline Funnel */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm" data-testid="pipeline-funnel">
              <p className="text-xs font-semibold text-[#0A1F44] mb-3 uppercase tracking-wider">Pipeline Funnel</p>
              <div className="flex gap-2">
                {PIPELINE_STAGES.map((stage, i) => {
                  const count = stageCounts[stage] || 0;
                  const colors = PIPELINE_COLORS[stage];
                  const isActive = stageFilter === stage;
                  return (
                    <button key={stage} onClick={() => setStageFilter(isActive ? 'all' : stage)}
                      data-testid={`funnel-${stage.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`flex-1 rounded-xl border-2 p-3 transition-all text-center ${
                        isActive ? 'ring-2 ring-[#D4AF37] shadow-md' : ''
                      } ${colors.bg}`}>
                      <p className={`text-2xl font-bold ${colors.text}`}>{count}</p>
                      <p className="text-[10px] font-semibold text-gray-500 mt-0.5">{stage}</p>
                      {i < PIPELINE_STAGES.length - 1 && <ArrowRight className="h-3 w-3 mx-auto mt-1 text-gray-300" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="status-filters">
              <Filter className="h-4 w-4 text-gray-400" />
              <span className="text-xs font-semibold text-gray-500">Response:</span>
              {['all', ...RESPONSE_STATUSES].map(s => (
                <button key={s} onClick={() => setStatusFilter(s)} data-testid={`status-${s.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-1 text-xs font-semibold rounded-full border transition-all ${
                    statusFilter === s
                      ? 'bg-[#0A1F44] text-white border-[#0A1F44]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#D4AF37]'
                  }`}>
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="outreach-table">
                  <thead className="bg-[#0A1F44] text-white">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs cursor-pointer" onClick={() => handleSort('company')}>
                        <div className="flex items-center gap-1">Company <SortIcon field="company" /></div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs">Pipeline Stage</th>
                      <th className="text-center px-3 py-3 text-xs">Response</th>
                      <th className="text-center px-3 py-3 text-xs">Contacted</th>
                      <th className="text-center px-3 py-3 text-xs">Last Outreach</th>
                      <th className="text-center px-3 py-3 text-xs">Next Follow-up</th>
                      <th className="text-center px-3 py-3 text-xs cursor-pointer" onClick={() => handleSort('updated_at')}>
                        <div className="flex items-center justify-center gap-1">Updated <SortIcon field="updated_at" /></div>
                      </th>
                      <th className="text-center px-3 py-3 text-xs">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((r, i) => {
                      const pColors = PIPELINE_COLORS[r.pipeline_stage] || PIPELINE_COLORS.Prospect;
                      const isOverdue = r.next_followup_date && new Date(r.next_followup_date) < new Date();
                      return (
                        <tr key={r.id || i} className="hover:bg-[#D4AF37]/5 transition-colors cursor-pointer" data-testid={`outreach-row-${i}`}
                          onClick={() => setSelectedCompany(r.company)}>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-[#0A1F44]">{r.company}</p>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`px-2.5 py-0.5 text-[10px] font-semibold rounded-full ${pColors.badge}`}>{r.pipeline_stage}</span>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <div className="flex items-center justify-center gap-1.5">
                              <div className={`w-2 h-2 rounded-full ${STATUS_DOTS[r.response_status] || 'bg-gray-400'}`} />
                              <span className="text-xs">{r.response_status}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-xs font-semibold ${r.contacted ? 'text-green-600' : 'text-gray-400'}`}>
                              {r.contacted ? 'Yes' : 'No'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center text-xs text-gray-600">{r.last_outreach_date || '—'}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                              {r.next_followup_date || '—'}
                              {isOverdue && ' (overdue)'}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-center text-[10px] text-gray-400">
                            {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <button onClick={(e) => { e.stopPropagation(); setSelectedCompany(r.company); }}
                              className="px-3 py-1 text-[10px] font-semibold bg-[#D4AF37] text-[#0A1F44] rounded-full hover:bg-[#D4AF37]/80"
                              data-testid={`manage-btn-${i}`}>
                              Manage
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filtered.length === 0 && (
                <div className="text-center py-16">
                  <Users className="h-10 w-10 mx-auto text-gray-300 mb-3" />
                  <p className="text-gray-400 text-sm">No outreach records yet</p>
                  <p className="text-gray-400 text-xs mt-1">Start tracking companies from the Opportunity Scores or Weekly Targets pages</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {selectedCompany && <CompanyDetailModal company={selectedCompany} onClose={handleModalClose} />}
    </IntelligenceLayout>
  );
};

export default OutreachCRM;
